import { Main, Models, StatusBadge, PrimaryButton, Assets } from 'utils/imports.utils';
import SelectDropdown from 'common_components/ui/select_dropdown/select_dropdown.ui';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetState, getDropdownData, getDropdownValue, getAdminUserLabel } from 'utils/functions.utils';
import { ButtonTypes } from 'utils/interface.utils';
import { AdminTicketDetail } from 'interfaces/admin.interface';
import AdminPage from 'common_components/ui/admin_page/admin_page.ui';
import './admin_ticket_detail.screen.scss';

const STATUS_OPTIONS = ['open', 'in_progress', 'resolved', 'closed'];

const getRequesterName = (user: AdminTicketDetail['ticket']['user']): string =>
  (user && getAdminUserLabel(user)) || 'Unknown';

const AdminTicketDetailScreen = () => {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const hasLoadedOnce = useRef(false);
  const [state, setState] = useSetState({
    detail: null as AdminTicketDetail | null,
    loading: true,
    replyMessage: '',
    isInternalNote: false,
    sending: false,
  });

  const fetchDetail = async () => {
    if (!ticketId) return;
    if (!hasLoadedOnce.current) {
      setState({ loading: true });
    }
    try {
      const response: any = await Models.auth.adminGetTicketDetail(ticketId);
      setState({ detail: response?.data || null, loading: false });
    } catch (err) {
      console.error('Failed to fetch ticket detail', err);
      setState({ loading: false });
    } finally {
      hasLoadedOnce.current = true;
    }
  };

  useEffect(() => {
    let token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchDetail();
  }, [ticketId]);

  const handleSendReply = async () => {
    if (!ticketId || !state.replyMessage.trim()) return;
    const wasInternalNote = state.isInternalNote;
    setState({ sending: true });
    try {
      await Models.auth.adminReplyTicket(ticketId, {
        message: state.replyMessage.trim(),
        is_internal_note: wasInternalNote,
      });
      // A ticket is a one-shot user report answered once by the admin (docs/PLAN.md "No
      // user-facing reply route") -- a regular reply is the admin's answer, so it resolves the
      // ticket. An internal note is not a reply to the requester, so it leaves status untouched.
      if (!wasInternalNote) {
        await Models.auth.adminSetTicketStatus(ticketId, { status: 'resolved' });
      }
      setState({ replyMessage: '', isInternalNote: false, sending: false });
      fetchDetail();
    } catch (err) {
      console.error('Failed to send reply', err);
      setState({ sending: false });
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!ticketId) return;
    try {
      await Models.auth.adminSetTicketStatus(ticketId, { status });
      fetchDetail();
    } catch (err) {
      console.error('Failed to update ticket status', err);
    }
  };

  return (
    <Main backgroundColor="#F7F3E9" isRenderOnlyLogo={false} isAdmin>
      <AdminPage breadcrumb="Admin / Tickets / Detail">
      <div className="admin_ticket_detail">
        <img
          src={Assets.arrow}
          alt="Back"
          className="admin_ticket_detail_back"
          onClick={() => navigate('/admin/tickets')}
        />

        {state.detail && (
          <>
            <div className="admin_ticket_detail_header">
              <div className="admin_ticket_detail_header_text">
                <h2>{state.detail.ticket.subject || 'Ticket details'}</h2>
                <span className="admin_ticket_detail_requester">
                  {state.detail.ticket.ticket_number}
                  {` · ${getRequesterName(state.detail.ticket.user)}`}
                </span>
              </div>
              {state.detail.ticket.status && (
                <StatusBadge
                  status={state.detail.ticket.status}
                />
              )}
            </div>

            <div className="admin_ticket_detail_columns">
              <div className="admin_ticket_detail_card">
                <h3 className="admin_ticket_detail_thread_title">Conversation</h3>
                <div className="admin_ticket_detail_reply admin_ticket_detail_reply_requester">
                  <span className="admin_ticket_detail_reply_author">{getRequesterName(state.detail.ticket.user)}</span>
                  <p className="admin_ticket_detail_description">{state.detail.ticket.description}</p>
                </div>
                {state.detail.replies.length === 0 ? (
                  <p className="admin_ticket_detail_no_replies">No replies yet</p>
                ) : (
                  <div className="admin_ticket_detail_thread">
                    {state.detail.replies.map((reply) => (
                      <div
                        key={reply._id}
                        className={`admin_ticket_detail_reply${
                          reply.is_internal_note ? ' admin_ticket_detail_reply_internal' : ''
                        }`}>
                        <span className="admin_ticket_detail_reply_author">
                          {reply.author_role === 'admin' ? 'Support' : 'Requester'}
                          {reply.is_internal_note ? ' (internal note)' : ''}
                        </span>
                        <p className="admin_ticket_detail_reply_message">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                {state.detail.ticket.status === 'resolved' || state.detail.ticket.status === 'closed' ? (
                  <p className="admin_ticket_detail_resolved_note">
                    This ticket is {state.detail.ticket.status}. Reopen it via the status dropdown to reply again.
                  </p>
                ) : (
                  <div className="admin_ticket_detail_reply_box">
                    <textarea
                      value={state.replyMessage}
                      onChange={(e) => setState({ replyMessage: e.target.value })}
                      placeholder="Type your reply..."
                      rows={4}
                    />
                    <div className="admin_ticket_detail_reply_box_actions">
                      <label className="admin_ticket_detail_internal_note_toggle">
                        <input
                          type="checkbox"
                          checked={state.isInternalNote}
                          onChange={(e) => setState({ isInternalNote: e.target.checked })}
                        />
                        Internal note
                      </label>
                      <PrimaryButton
                        text="Send reply"
                        type={ButtonTypes.PRIMARY}
                        onClick={handleSendReply}
                        activity={state.sending}
                        disabled={!state.replyMessage.trim()}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="admin_ticket_detail_card admin_ticket_detail_details">
                <h3>Details</h3>
                <div>
                  <span className="admin_ticket_detail_label">Status</span>
                  <div className="admin_ticket_detail_status_select">
                    <SelectDropdown
                      data={getDropdownData(STATUS_OPTIONS)}
                      value={getDropdownValue(state.detail.ticket.status || '')}
                      onChange={(selectedOption: any) => selectedOption?.value && handleStatusChange(selectedOption.value)}
                      isFilter
                      width="160px"
                      height="40px"
                    />
                  </div>
                </div>
                <div>
                  <span className="admin_ticket_detail_label">Ticket</span>
                  <span className="admin_ticket_detail_number">{state.detail.ticket.ticket_number}</span>
                </div>
                <div>
                  <span className="admin_ticket_detail_label">Requester</span>
                  {getRequesterName(state.detail.ticket.user)}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      </AdminPage>
    </Main>
  );
};

export default AdminTicketDetailScreen;
